import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Divider,
  Typography,
} from '@mui/material';
import { PersonAdd } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { ROLE_KEYS, roleLabel } from '../components/actionables/roles';
import { eyebrow } from '../components/actionables/brand';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';

// House outlined-input treatment shared with the other migrated pages (InventoryPage): rounded
// corners + white fill. 10px here (not the 999px search pill) because these are form fields.
const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } } as const;

// Salty accounts must never be created with a password here — they sign in with Google and go
// through the access-request + admin-approval flow. The BACKEND is the authoritative guard
// (POST /api/auth/create rejects any configured Salty domain via isSaltyEmail); this is only a
// client-side convenience check against the known primary domain so the user gets inline
// feedback before submitting. The server still rejects any other configured Salty domain.
const SALTY_DOMAIN = 'salty.co.in';
const isSaltyEmail = (value: string): boolean => {
  const at = value.lastIndexOf('@');
  if (at === -1) return false;
  return value.slice(at + 1).trim().toLowerCase() === SALTY_DOMAIN;
};

const AdminCreateUser: React.FC = () => {
  const { createUser } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>('warehouse-manager');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // RBAC: user creation is manage-users:edit on the backend; mirror that gate here (replaces
  // the retired super-admin role-string check). Admins short-circuit to true in the hook.
  const canManageUsers = usePermission('manage-users').can('edit');

  // A Salty email entered here is invalid: those users must use Google sign-in + request access.
  const emailIsSalty = isSaltyEmail(email.trim());

  const handleCreate = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      if (!canManageUsers) {
        throw new Error('You do not have permission to create users');
      }
      if (emailIsSalty) {
        throw new Error(
          `${SALTY_DOMAIN} accounts can't be created here. Salty users must sign in with Google and request access with a reason, which an admin then approves.`,
        );
      }
      await createUser(email, name, password, role);
      setMessage('User created successfully');
      setEmail(''); setName(''); setPassword('');
      setRole('warehouse-manager');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        icon={<PersonAdd />}
        title="Create User"
        subtitle={
          <>
            Create a password-based account for a non-Salty user. Only users with Manage Users
            access can create users, and only for non-Salty email addresses. Salty accounts must
            sign in with Google and cannot be created here.
          </>
        }
      />

      <Box sx={{ width: '100%' }}>
        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #ECECE4',
            bgcolor: '#fff',
            boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 10px 28px rgba(26,20,15,0.06)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography sx={{ ...eyebrow, color: '#8A8078' }}>Account details</Typography>
            <Divider sx={{ mt: 1.25, mb: 2.5, borderColor: '#ECECE4' }} />

            {message && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}
            {!canManageUsers && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>You do not have permission to create new users.</Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  value={role}
                  label="Role"
                  onChange={(e) => setRole(e.target.value as string)}
                  disabled={!canManageUsers}
                  MenuProps={{ PaperProps: { style: { zIndex: 1400 } } }}
                >
                  {ROLE_KEYS.map((r) => (
                    <MenuItem key={r} value={r}>{roleLabel(r)}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={fieldSx}
                error={emailIsSalty}
                helperText={
                  emailIsSalty
                    ? `${SALTY_DOMAIN} users can't be created here — they sign in with Google and request access for admin approval.`
                    : undefined
                }
              />
              <TextField label="Name" fullWidth value={name} onChange={(e) => setName(e.target.value)} sx={fieldSx} />
              <TextField label="Password" type="password" fullWidth value={password} onChange={(e) => setPassword(e.target.value)} sx={fieldSx} />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                fullWidth
                onClick={handleCreate}
                disabled={loading || !canManageUsers || !email || !name || !password || emailIsSalty}
                sx={{
                  py: 1.1,
                  px: 2.5,
                  fontWeight: 800,
                  textTransform: 'none',
                  borderRadius: '999px',
                  '&:hover': {
                    boxShadow: '0px 4px 12px rgba(220, 88, 41, 0.3)',
                  },
                }}
              >
                {loading ? 'Creating...' : 'Create User'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </PageContainer>
  );
};

export default AdminCreateUser;
